import { lazy, ReactNode, Suspense, ComponentType } from 'react';

import { retryFetch } from '@app/app/lib/api';
import { LoadingScreen } from '@app/components/template';

import { LazyLoadErrorFallback } from '../ui';

import { logChunkLoadError } from './chunkLogger';

// Wrapper для lazy компонентов с retry механизмом и логированием только ошибок
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const lazyWithErrorBoundary = <T extends ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>,
  componentName?: string,
): React.LazyExoticComponent<T> => {
  return lazy(() => {
    const startTime = performance.now();
    const loadId = `${componentName || 'unknown'}_${Date.now()}`;
    const maxAttempts = 3;

    return retryFetch({
      cb: importFunc,
      retries: 2, // 3 попытки = 2 retry + 1 изначальная
      delayBetween: 500, // Увеличиваем задержку для нестабильного интернета
    }).catch((error) => {
      const loadTime = performance.now() - startTime;

      // Логируем только ошибки с деталями по попыткам
      logChunkLoadError({
        error,
        componentName: componentName || 'unknown',
        loadId,
        loadTime,
        attempt: maxAttempts,
        maxAttempts,
      });

      return {
        default: (() => (
          <LazyLoadErrorFallback
            error={error instanceof Error ? error : new Error('Lazy loading error')}
          />
        )) as unknown as T,
      };
    });
  });
};

export const loadWithSuspense = (component: ReactNode) => (
  <Suspense fallback={<LoadingScreen />}>{component}</Suspense>
);
